import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import { MediaserverVideoSettingResponse } from 'features/mediaserver-settings/domain/models/mediaserver-video-setting-response';
import { UpdateMediaserverVideoSettingCommand } from 'features/mediaserver-settings/domain/models/update-mediaserver-video-setting-command';

const baseUrl = '/api/v1/Settings';

const readMediaserverVideoSetting = async (axiosInstance: AxiosInstance): Promise<MediaserverVideoSettingResponse> => {
  try {
    const response = await axiosInstance.get(`${baseUrl}/ReadMediaserverVideoSetting`);

    return response.data as MediaserverVideoSettingResponse;
  } catch (error) {
    throw new Error('Error while calling ReadMediaserverVideoSetting', {
      cause: error,
    });
  }
};

const updateMediaserverVideoSetting = async (axiosInstance: AxiosInstance, command: UpdateMediaserverVideoSettingCommand) => {
  const requestBody = JSON.stringify(command);
  const response = await axiosInstance.put(`${baseUrl}/UpdateMediaserverVideoSetting`, requestBody);
  return response.data;
};

export const useReadMediaserverVideoSettingQuery = (): UseQueryResult<MediaserverVideoSettingResponse> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: ['ReadMediaserverVideoSetting'],
    queryFn: () => readMediaserverVideoSetting(authenticatedInstance),
  });
};

export const useUpdateMediaserverVideoSettingMutation = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['UpdateMediaserverVideoSetting'],
    mutationFn: (updateMediaserverVideoSettingCommand: UpdateMediaserverVideoSettingCommand) =>
      updateMediaserverVideoSetting(authenticatedInstance, updateMediaserverVideoSettingCommand),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ReadMediaserverVideoSetting'],
      });
    },
  });
};
